import { keycloakLogin } from '@/auth/keycloak';
import { env, isProdDeploy } from '@/env';
import { openExternal } from '@/lib/open-external';
import { useAuthStore } from '@/stores/auth.store';
import { Capacitor } from '@capacitor/core';
import { Page, Preloader, f7 } from 'framework7-react';
import { useState } from 'react';
// Master app icon (same source @capacitor/assets generates the native icons from),
// so the sign-in mark matches the installed app's home-screen icon exactly.
import appIcon from '../../../../assets/icon.png';

// Public self-serve onboarding wizard. Any restaurant can register here - opened
// in the system browser (it's a separate web app), so the dashboard stays a
// pure sign-in surface for existing operators.
const ONBOARDING_URL = env.onboardingUrl || 'https://onboarding.96gastro.de';

// The native app is sign-in ONLY. New-restaurant registration lives on the web
// (onboarding.96gastro.de); we never surface a business signup flow inside the
// iOS/Android app, per App Store Guideline 3.1.1 (in-app business registration
// reads as an external purchase mechanism). Web build keeps the register CTA.
const SHOW_REGISTER = !Capacitor.isNativePlatform();

/** Dev-only quick sign-in. Gated by import.meta.env.DEV so it never ships to operators. */
const DEMO_ACCOUNTS = [
  { label: 'Admin', email: 'admin@test.com', password: 'admin' },
  { label: 'Staff', email: 'staff@test.com', password: 'staff' },
] as const;

// Prod-only one-tap sign-in to the shared demo tenant. These credentials are
// intentionally public, so there is no secret to protect by routing through the
// backend. Gated by isProdDeploy so it exists ONLY on the production host.
const REVIEWER_ACCOUNT = { email: 'apple-review@96gastro.de', password: '1234567890' } as const;

function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (creds?: { email: string; password: string }) => {
    const e = (creds?.email ?? email).trim();
    const p = creds?.password ?? password;
    if (!e || !p) {
      setError('Enter your email and password.');
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      const tokens = await keycloakLogin(e, p);
      login(
        {
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
        },
        e,
      );
      // Navigation unmounts this page; leave the spinner running through it.
      f7.views.main.router.navigate('/', { reloadAll: true });
    } catch {
      // Inline, non-blocking error - never a dialog (ERROR_HANDLING.md).
      setError('That email or password didn’t work. Please try again.');
      setIsLoading(false);
    }
  };

  const clearError = () => {
    if (error) setError(null);
  };

  return (
    <Page name="login" noNavbar noToolbar>
      <div className="login-page">
        <div className="login-card">
          <div className="login-brand">
            <div className="login-brand__mark">
              <img className="login-brand__icon" src={appIcon} alt="96Gastro" />
            </div>
            <p className="login-brand__subtitle">Sign in to manage your restaurant</p>
          </div>

          <form
            className="login-form"
            onSubmit={(ev) => {
              ev.preventDefault();
              void submit();
            }}
          >
            <label className="login-field">
              <span className="login-field__label">Email</span>
              <input
                className="login-input"
                type="email"
                name="email"
                placeholder="you@restaurant.com"
                autoComplete="username"
                value={email}
                disabled={isLoading}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearError();
                }}
              />
            </label>

            <label className="login-field">
              <span className="login-field__label">Password</span>
              <div className="login-input-wrap">
                <input
                  className="login-input login-input--password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  disabled={isLoading}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearError();
                  }}
                />
                <button
                  type="button"
                  className="login-reveal"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <i className="f7-icons">{showPassword ? 'eye_slash' : 'eye'}</i>
                </button>
              </div>
            </label>

            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="login-submit" disabled={isLoading}>
              {isLoading ? <Preloader color="white" size={22} /> : 'Sign In'}
            </button>
          </form>

          {SHOW_REGISTER && (
            <div className="login-register">
              <span className="login-register__text">New to 96Gastro?</span>
              <button
                type="button"
                className="login-register__btn"
                onClick={() => openExternal(ONBOARDING_URL)}
              >
                Register your restaurant
                <i className="f7-icons login-register__icon">arrow_up_right</i>
              </button>
            </div>
          )}

          {isProdDeploy && (
            <div className="login-demo">
              <button
                type="button"
                className="login-demo__reviewer"
                disabled={isLoading}
                onClick={() => void submit({ ...REVIEWER_ACCOUNT })}
              >
                Sign in with demo account
              </button>
            </div>
          )}

          {import.meta.env.DEV && (
            <div className="login-demo">
              <span className="login-demo__label">Demo accounts</span>
              <div className="login-demo__chips">
                {DEMO_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    className="login-demo__chip"
                    disabled={isLoading}
                    onClick={() => void submit({ email: a.email, password: a.password })}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export default LoginPage;
